export type SessionStatus = 'running' | 'waiting' | 'interrupted' | 'done'

export interface DiffHunk {
  file: string
  added: number
  removed: number
  snippet?: string
}

export interface ToolCall {
  id: string
  name: string
  input: string
  output?: string
  diff?: DiffHunk
}

export interface CodeMessage {
  id: string
  role: 'user' | 'agent' | 'system'
  content: string
  toolCalls?: ToolCall[]
  timestamp: string
}

export interface FinalActionChoice {
  key: string
  label: string
}

export interface FinalAction {
  kind: 'choice' | 'open'
  question: string
  choices?: FinalActionChoice[]
}

export type CodeArtifactKind = 'pages-updated' | 'test-results' | 'hitl-step' | 'preview-url'

interface BaseArtifact {
  id: string
  sessionId: string
  title: string
  timestamp: string
  status: 'pending' | 'resolved'
}

export interface PagesUpdatedArtifact extends BaseArtifact {
  kind: 'pages-updated'
  files: { path: string; added: number; removed: number; snippet?: string }[]
}

export interface TestResultsArtifact extends BaseArtifact {
  kind: 'test-results'
  passed: number
  failed: number
  failingTests: { name: string; error: string }[]
}

export interface HitlStepArtifact extends BaseArtifact {
  kind: 'hitl-step'
  question: string
  mode: 'choice' | 'open'
  choices?: FinalActionChoice[]
  answer?: string
}

export interface PreviewUrlArtifact extends BaseArtifact {
  kind: 'preview-url'
  url: string
  description: string
}

export type CodeArtifact =
  | PagesUpdatedArtifact
  | TestResultsArtifact
  | HitlStepArtifact
  | PreviewUrlArtifact

export interface CodeSession {
  id: string
  targetId: string
  title: string
  originalRequest: string
  createdLabel: string
  startedAt: string
  status: SessionStatus
  messages: CodeMessage[]
  finalAction?: FinalAction
  artifactIds: string[]
}

export interface CodeTarget {
  id: string
  projectId: string
  name: string
}

export interface CodeProject {
  id: string
  name: string
  targets: CodeTarget[]
}

export const STATUS_LABEL: Record<SessionStatus, string> = {
  running: 'running',
  waiting: 'waiting',
  interrupted: 'interrupted',
  done: 'done',
}

// All session statuses use the same cyan — state is conveyed by label + subtle
// opacity/animation, not hue.
export const CYAN = '#4ac0d4'

export const STATUS_COLOR: Record<SessionStatus, string> = {
  running: CYAN,
  waiting: CYAN,
  interrupted: CYAN,
  done: CYAN,
}
